import type { ReactNode, RefObject } from 'react'
import React, { createContext, useContext, useRef, useCallback } from 'react'

interface SkipNavigationContextProps {
  registerRef: (id: string) => (node: HTMLElement | null) => void
  scrollToAndFocus: (id: string) => void
}

interface SkipNavigationProviderProps {
  children: ReactNode
}

export enum ReferenceName {
  SIDE_NAVIGATION = 'navigation',
  ACCOUNT_BACK_BUTTON = 'account-back-button',
}

const SkipNavigationContext = createContext<SkipNavigationContextProps | null>(null)

export const SkipNavigationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}: SkipNavigationProviderProps) => {
  const registry: RefObject<Map<string, HTMLElement>> = useRef<Map<string, HTMLElement>>(new Map())

  const registerRef: (id: string) => (node: HTMLElement | null) => void = useCallback(
    (id: string) => (node: HTMLElement | null) => {
      if (node) {
        registry.current.set(id, node)
      } else {
        registry.current.delete(id)
      }
    },
    []
  )

  const scrollToAndFocus: (id: string) => void = useCallback((id: string) => {
    const element = registry.current.get(id)
    if (element) {
      element.focus()
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }, [])

  return (
    <SkipNavigationContext.Provider value={{ registerRef, scrollToAndFocus }}>
      {children}
    </SkipNavigationContext.Provider>
  )
}

export const useSkipNavigation: () => SkipNavigationContextProps = () => {
  const context = useContext(SkipNavigationContext)
  if (!context) {
    throw new Error('useSkipNavigation must be used within a SkipNavigationProvider')
  }
  return context
}
